import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CmsBanner from "../../components/Banner/CmsBanner";
import Loader from "../../components/Loader/Loader";
import { getNotificationDetailAPI } from "../../api/notification";
import dayjs from "dayjs";
import { FaBell, FaArrowLeft, FaArrowRight, FaGamepad, FaShoppingBag } from "react-icons/fa";

export default function NotificationDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isRtl = i18n.language === "ar";

  // Fetch notification detail (endpoint also updates is_read to true)
  const { data: notification, isLoading, isError } = useQuery({
    queryKey: ["notificationDetail", id],
    queryFn: () => getNotificationDetailAPI(id!),
    enabled: !!id,
  });

  const handleActionClick = () => {
    if (!notification) return;

    // Action redirection logic based on notification type
    if (notification.type === "purchase") {
      navigate("/my-games");
    } else if (notification.type === "no_games_left") {
      navigate("/package-list");
    } else {
      navigate("/notifications");
    }
  };

  const title = notification
    ? (notification.title
      ? (typeof notification.title === "string" ? notification.title : (notification.title[i18n.language] || notification.title.en || ""))
      : "")
    : "";
  const body = notification
    ? (notification.body
      ? (typeof notification.body === "string" ? notification.body : (notification.body[i18n.language] || notification.body.en || ""))
      : "")
    : "";
  const formattedDate = notification ? dayjs(notification.created_at).locale(i18n.language).format("LLLL") : "";

  return (
    <>
      <CmsBanner title={t("notifications.notificationDetails")} />

      <div className="w-full bg-[#F7F7F7] min-h-[calc(100vh-240px)] py-6 md:py-10 px-4 md:px-8">
        <div className="max-w-[700px] mx-auto bg-white border-2 border-black-300 rounded-2xl shadow-[0px_4px_0px_#110706] p-4 md:p-6 skew-x-[-2deg]">
          <div className="skew-x-[2deg] flex flex-col">
            {/* Back Button */}
            <button
              onClick={() => navigate("/notifications")}
              className="flex items-center gap-2 w-fit px-3 py-1.5 border-2 border-black-300 bg-white hover:bg-[#FAF9F5] rounded-lg font-bold text-sm shadow-[0px_2px_0px_#110706] active:translate-y-[2px] active:shadow-none transition-all mb-6"
            >
              {isRtl ? <FaArrowRight /> : <FaArrowLeft />}
              <span>{t("notifications.back")}</span>
            </button>

            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <Loader />
              </div>
            ) : isError || !notification ? (
              <div className="text-center py-10">
                <h3 className="text-lg font-bold text-primary-red">
                  Failed to load notification details.
                </h3>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                {/* Visual Header card */}
                <div className="flex items-center gap-4 p-5 bg-[#FFFAF0] border-2 border-black-300 rounded-xl shadow-[0px_3px_0px_#110706]">
                  <div className="w-14 h-14 rounded-full border-2 border-black-300 bg-primary-yellow flex items-center justify-center shrink-0">
                    {notification.type === "purchase" ? (
                      <FaGamepad className="text-2xl text-black-300" />
                    ) : notification.type === "no_games_left" ? (
                      <FaShoppingBag className="text-2xl text-black-300" />
                    ) : (
                      <FaBell className="text-2xl text-black-300" />
                    )}
                  </div>
                  
                  <div className="min-w-0">
                    <h2 className="text-lg md:text-xl font-extrabold text-black-300 leading-snug">
                      {title}
                    </h2>
                    <span className="text-xs text-black-500 font-bold mt-1 block">
                      {formattedDate}
                    </span>
                  </div>
                </div>

                {/* Body Content */}
                <div className="p-5 border-2 border-black-300 rounded-xl bg-white min-h-[120px] shadow-[0px_3px_0px_#110706]">
                  <p className="text-black-300 font-medium text-base md:text-lg leading-relaxed whitespace-pre-wrap">
                    {body}
                  </p>
                </div>

                {/* Action button redirection based on type */}
                {(notification.type === "purchase" || notification.type === "no_games_left") && (
                  <button
                    onClick={handleActionClick}
                    className="w-full py-3.5 border-2 border-black-300 bg-primary-orange hover:bg-primary-orange/95 text-black-300 hover:text-black-300 rounded-xl font-black text-base shadow-[0px_4px_0px_#110706] active:translate-y-[3px] active:shadow-[0px_1px_0px_#110706] transition-all text-center flex justify-center items-center gap-2"
                  >
                    {notification.type === "purchase" ? (
                      <>
                        <FaGamepad className="text-lg" />
                        <span>{isRtl ? "عرض ألعابي" : "View My Games"}</span>
                      </>
                    ) : (
                      <>
                        <FaShoppingBag className="text-lg" />
                        <span>{isRtl ? "شراء باقة" : "Buy a Package"}</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
